/**
 * MeasurementCapture — iter150AG photo-only voltage/amps capture.
 *
 * One instance per measurement slot (e.g. "voltage running L1↔L2",
 * "amps running L1", "voltage stopped (1-phase)"). The operator:
 *   1. Taps the capture surface → device camera opens.
 *   2. Picks a photo. Component POSTs to /api/measurements (multipart)
 *      and shows the AI-extracted value while saving.
 *   3. Accepts the AI value, retakes the photo, or types a manual
 *      override (override is flagged on the record for audit).
 *
 * Per the user's design rules:
 *   - Manual typing of the value WITHOUT a photo is never possible.
 *   - The AI's confidence and rationale are surfaced so operators can
 *     spot bad reads.
 *   - Low-confidence reads (<0.7) are saved but visually flagged so
 *     QA can revisit later.
 */
import { useEffect, useRef, useState } from "react";
import {
  Camera, ArrowsClockwise, CheckCircle, Warning, CircleNotch,
  Pencil, X, ImageSquare, CloudArrowUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useMeasurementWs } from "@/lib/useMeasurementWs";
import useOfflineQueue from "@/lib/useOfflineQueue";
import {
  slotKey as _slotKey,
  isMeasurementsSetComplete as _isMeasurementsSetComplete,
} from "./measurementCompletion";

const _API = process.env.REACT_APP_BACKEND_URL || "";

// Re-export the pure helper so existing callers keep working.
export const isMeasurementsSetComplete = _isMeasurementsSetComplete;


// _slotKey is imported from ./measurementCompletion above.


export default function MeasurementCapture({
  parentKind,            // "commissioning" | "warranty_claim"
  parentId,              // FK to the parent record
  kind,                  // "voltage" | "amps"
  state,                 // "running" | "stopped"
  phase,                 // "l1_l2" | "l2_l3" | "l1_l3" | "l1" | "l2" | "l3" | "single"
  label,                 // human-readable label e.g. "L1 ↔ L2 (running)"
  nominalHint,           // optional float (e.g. 240) — helps the AI
  initialRecord,         // if pre-existing measurement row, pre-fill state
  onSaved,               // (record) => void — fired after every successful save
  onCleared,             // () => void — fired after delete
  required,              // bool (controls the "required" badge)
  email,                 // current user's email (for X-User-Email)
  disabled,              // bool (read-only)
}) {
  const [record, setRecord]       = useState(initialRecord || null);
  const [uploading, setUploading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [editingOverride, setEditingOverride] = useState(false);
  const [overrideInput, setOverrideInput]     = useState("");
  const [overrideReason, setOverrideReason]   = useState("");
  // iter150AK — AI extraction is async. We poll /measurements/{id}/status
  // until ai_status flips to "done" or "failed".
  const [aiPending, setAiPending] = useState(false);
  // iter150AZ.77 — offline awareness. When the device is offline we
  // route the upload through the SW outbox and synthesise a local
  // stub record so the wizard's Continue gate can satisfy without
  // any server roundtrip. AI / approval / override actions are
  // suppressed for offline stubs because their PATCH would race the
  // queued POST replay (the stub's id doesn't exist on the server
  // yet).
  const { online, enqueue } = useOfflineQueue();
  const isOfflineRecord = !!record?._offline;
  const inputRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => { setRecord(initialRecord || null); }, [initialRecord]);

  // Auto-resume polling if the parent loaded a record that's still
  // mid-extraction. This handles the case where the user navigated
  // away and back while the background job was running.
  // The setState call happens via a scheduled microtask so we
  // don't trip the react-hooks/set-state-in-effect rule.
  useEffect(() => {
    const r = initialRecord;
    if (r?._offline) return undefined;              // offline stubs have no server-side AI job
    if (r?.id && (r.ai_status === "pending" || r.ai_status === "processing")) {
      const handle = setTimeout(() => startAiPoll(r.id), 0);
      return () => clearTimeout(handle);
    }
    return undefined;
  }, [initialRecord?.id]);

  // Stop the poll loop if the component unmounts mid-extraction.
  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

  const unit       = kind === "voltage" ? "V" : "A";
  const slotKey    = _slotKey(kind, state, phase);
  const hasPhoto   = !!record;
  const finalValue = record?.final_value;
  const aiValue    = record?.ai_value;
  const conf       = record?.ai_confidence;
  const isOverride = !!record?.manual_override;
  const isLowConf  = !!record?.low_confidence;
  const aiStatus   = record?.ai_status;     // "pending" | "processing" | "done" | "failed"
  // iter150AZ.63 — mandatory operator approval. Backend sets
  // ``approved=true`` automatically when the operator uses an
  // override (typing IS approval); the AI-only path requires the
  // operator to click "Approve Reading" before the wizard's
  // Continue button enables.
  // iter150AZ.77 — offline stubs are auto-approved (no server to
  // ask), so the approval gate doesn't render for them.
  const isApproved = !!record?.approved;
  const needsApproval = hasPhoto
    && !isApproved
    && !isOfflineRecord
    && finalValue != null
    && aiStatus !== "pending"
    && aiStatus !== "processing";
  const photoUrl   = record?._local_photo_url
    ? record._local_photo_url
    : (record?.id && !isOfflineRecord)
      ? `${_API}/api/measurements/${record.id}/photo`
      : null;

  // Poll the status endpoint every ~1.5s until the background extraction
  // resolves (or 30s timeout — operator can always retake). Cheap call:
  // status endpoint returns a small projection, not the photo.
  // iter150AL — the WS push usually beats the first poll, but polling
  // stays as the always-on fallback for proxy/firewall environments
  // that block WebSocket upgrades.
  const _applyResolved = (s) => {
    if (!s) return;
    setAiPending(false);
    if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
    setRecord(prev => ({ ...(prev || {}), ...s }));
    onSaved?.({ ...(record || {}), ...s });
    if (s.ai_status === "failed") {
      toast.error(`AI extraction failed${s.ai_error ? `: ${s.ai_error}` : ""}. Use manual override or retake.`);
    } else if (s.ai_value == null) {
      toast.warning(`AI couldn't read this ${kind} photo. Retake or use manual override.`);
    } else if (s.low_confidence) {
      toast.warning(`AI extracted ${s.final_value} ${unit} but confidence is low. Verify or retake.`);
    } else if (!s.manual_override) {
      toast.success(`AI read ${s.final_value} ${unit}`);
    }
  };

  const startAiPoll = (mid) => {
    if (!mid) return;
    setAiPending(true);
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/measurements/${mid}/status`);
        const s = r.data;
        if (s?.ai_status === "done" || s?.ai_status === "failed") {
          _applyResolved(s);
          return;
        }
        // still pending/processing — schedule next tick (up to ~30s)
        if (attempts < 20) {
          pollRef.current = setTimeout(tick, 1500);
        } else {
          setAiPending(false);
          toast.warning("AI is taking unusually long. Use manual override if it doesn't finish.");
        }
      } catch (e) {
        // Network blip — back off and retry a few times before giving up.
        if (attempts < 20) {
          pollRef.current = setTimeout(tick, 2500);
        } else {
          setAiPending(false);
        }
      }
    };
    pollRef.current = setTimeout(tick, 1200);   // first tick after ~1.2s
  };

  // iter150AL — listen on the shared WebSocket for an immediate push.
  // Pick out only events for THIS slot (the same WS fans out events
  // for every measurement under the parent record). If the WS push
  // arrives first, the next scheduled poll tick is a cheap no-op
  // because we cleared the timer.
  useMeasurementWs(
    aiPending && record?.id ? parentKind : null,
    aiPending && record?.id ? parentId : null,
    email,
    (msg) => {
      const r = msg?.record;
      if (!r || r.id !== record?.id) return;
      if (r.ai_status === "done" || r.ai_status === "failed") {
        _applyResolved(r);
      }
    }
  );

  async function uploadPhoto(file) {
    if (!file) return;
    if (disabled) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("parent_kind", parentKind);
      fd.append("parent_id",   parentId);
      fd.append("kind",        kind);
      fd.append("state",       state);
      fd.append("phase",       phase);
      if (nominalHint) fd.append("nominal_hint", String(nominalHint));
      fd.append("file", file);
      // iter150AZ.77 — offline-first. Ask the SW outbox first; when
      // disconnected it persists the multipart body (Blob + fields)
      // and returns "queued". We synthesise a local stub with
      // ``_offline:true`` + ``approved:true`` so the wizard's
      // ``isMeasurementsSetComplete`` gate goes green. The server's
      // upsert-by-(parent_kind, parent_id, slot) collapses any
      // retake replays into the latest photo per slot.
      const url = `${_API}/api/measurements`;
      const decision = await enqueue(url, {
        method: "POST",
        headers: { "X-User-Email": email || "" },
        body:   fd,
      });
      if (decision === "queued") {
        // Revoke the previous local blob URL (retake case).
        if (record?._local_photo_url) {
          try { URL.revokeObjectURL(record._local_photo_url); } catch { /* nonfatal */ }
        }
        const localUrl = (typeof URL !== "undefined" && URL.createObjectURL)
          ? URL.createObjectURL(file) : null;
        const stub = {
          id:               `offline-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          parent_kind:      parentKind,
          parent_id:        parentId,
          slot:             slotKey,
          kind, state, phase,
          phase_label:      phase,
          ai_value:         null,
          ai_unit:          unit,
          ai_confidence:    null,
          ai_low_confidence: false,
          ai_notes:         null,
          ai_status:        "offline_pending",
          ai_error:         null,
          manual_override:  false,
          manual_value:     null,
          override_reason:  null,
          final_value:      null,
          low_confidence:   false,
          // Auto-approved offline so the wizard can advance. AI / QA
          // review happens once the photo syncs server-side.
          approved:         true,
          approved_by:      email || null,
          approved_at:      new Date().toISOString(),
          _offline:         true,
          _local_photo_url: localUrl,
        };
        setRecord(stub);
        onSaved?.(stub);
        toast.info("Photo saved offline — will sync when you're back online");
        return;
      }
      // iter150AJ — switched from raw fetch() to axios. iOS Safari
      // throws "Body is disturbed or locked" on Response.json() when
      // the page backgrounds during the 3-5s Claude vision wait —
      // the response body stream gets "disturbed" by Safari's network
      // suspension logic. Axios uses XHR which doesn't expose the
      // stream API and isn't affected by this Safari bug.
      const resp = await api.post(`/measurements`, fd, {
        headers: { "Content-Type": "multipart/form-data",
                   "X-User-Email": email || "" },
        // Auth (X-User-Email) is also auto-attached by api.js's
        // request interceptor from localStorage; the explicit header
        // here keeps backward compat for sessions where the email
        // wasn't persisted yet.
      });
      const data = resp.data;
      // Revoke any blob URL from a prior offline stub now that the
      // server has the photo and the next render will use the
      // /api/measurements/{id}/photo route.
      if (record?._local_photo_url) {
        try { URL.revokeObjectURL(record._local_photo_url); } catch { /* nonfatal */ }
      }
      setRecord(data);
      onSaved?.(data);
      // iter150AK — AI extraction now runs in the background. The POST
      // returns immediately with ``ai_status="pending"``; we start
      // polling /measurements/{id}/status so the spinner flips to the
      // resolved reading without holding the upload connection open
      // (which used to trigger Safari iOS "Body is disturbed or locked"
      // when the tab backgrounded during the 3-5s Claude call).
      if (data?.id && data?.ai_status && data.ai_status !== "done") {
        startAiPoll(data.id);
      }
    } catch (e) {
      // Server returned a non-2xx OR network failure. Surface the
      // backend ``detail`` field when present (e.g. "Image too large",
      // "Unsupported image format"); fall back to a generic message
      // otherwise. The global axios interceptor in lib/api.js still
      // toasts 5xx/network errors with an incident id; this local
      // toast covers 4xx with a friendlier copy.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (detail) {
        toast.error(detail);
      } else if (status) {
        toast.error(`Upload failed (${status})`);
      } else {
        toast.error(e?.message || "Upload failed");
      }
    } finally {
      setUploading(false);
    }
  }

  async function submitOverride() {
    const v = parseFloat(overrideInput);
    if (!isFinite(v)) {
      toast.error("Enter a numeric value");
      return;
    }
    if (!record?.id) {
      toast.error("Take a photo first — manual override requires the photo for audit");
      return;
    }
    if (isOfflineRecord) {
      // The server doesn't know about this measurement yet — its id
      // is a client-minted "offline-..." stub. The queued POST will
      // create the row on sync; we can't PATCH it before then.
      toast.info("You're offline — retake the photo to record a different value, or edit it from history once you reconnect.");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("manual_value", String(v));
      if (overrideReason.trim()) fd.append("override_reason", overrideReason.trim());
      const resp = await api.patch(`/measurements/${record.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data",
                   "X-User-Email": email || "" },
      });
      const data = resp.data;
      setRecord(data);
      onSaved?.(data);
      setEditingOverride(false);
      toast.success(`Manual override: ${v} ${unit}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Override failed");
    }
  }

  async function clearOverride() {
    if (!record?.id) return;
    if (isOfflineRecord) return;          // nothing to clear on the server yet
    try {
      const fd = new FormData();
      fd.append("clear_override", "true");
      const resp = await api.patch(`/measurements/${record.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data",
                   "X-User-Email": email || "" },
      });
      const data = resp.data;
      setRecord(data);
      onSaved?.(data);
      toast.success("Reverted to AI reading");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Clear failed");
    }
  }

  async function remove() {
    if (!record?.id) return;
    if (!window.confirm(`Delete this ${label || kind} photo?`)) return;
    if (isOfflineRecord) {
      // The server doesn't have this measurement yet — clear the
      // local stub. The queued POST in the SW outbox is still
      // there; it would resurrect the row on reconnect. We can't
      // cancel that without a SW message we don't have. Best UX
      // is to surface this honestly so the user knows.
      if (record._local_photo_url) {
        try { URL.revokeObjectURL(record._local_photo_url); } catch { /* nonfatal */ }
      }
      setRecord(null);
      onCleared?.();
      toast.warning(
        "Cleared locally. Note: the photo is still queued and will sync when online — delete it from history once it appears.",
        { duration: 7000 },
      );
      return;
    }
    try {
      await api.delete(`/measurements/${record.id}`, {
        headers: { "X-User-Email": email || "" },
      });
      setRecord(null);
      onCleared?.();
      toast.success("Measurement cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Delete failed");
    }
  }

  // iter150AZ.63 — operator approval. Sends ``approve=true`` to the
  // PATCH endpoint; backend stamps ``approved/approved_by/approved_at``
  // so the wizard's Continue gate (isMeasurementsSetComplete) flips
  // green for this slot.
  async function approveReading() {
    if (!record?.id) return;
    if (isOfflineRecord) return;          // already auto-approved locally
    if (finalValue == null) {
      toast.error("No reading to approve yet — wait for AI or use override");
      return;
    }
    setApproving(true);
    try {
      const fd = new FormData();
      fd.append("approve", "true");
      const resp = await api.patch(`/measurements/${record.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data",
                   "X-User-Email": email || "" },
      });
      const data = resp.data;
      setRecord(data);
      onSaved?.(data);
      toast.success(`Approved: ${data.final_value} ${unit}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Approval failed");
    } finally {
      setApproving(false);
    }
  }

  return (
    <div data-testid={`meas-${slotKey}`}
         data-approved={isApproved ? "1" : "0"}
         data-needs-approval={needsApproval ? "1" : "0"}
         data-offline={isOfflineRecord ? "1" : "0"}
         className={`bg-white border rounded-sm p-3 ${
           hasPhoto && isOfflineRecord     ? "border-sky-300 ring-1 ring-sky-100"
             : hasPhoto && needsApproval        ? "border-violet-400 ring-1 ring-violet-200"
             : hasPhoto && isApproved && !isLowConf && !isOverride ? "border-emerald-300"
             : hasPhoto && isApproved && isOverride ? "border-amber-300"
             : hasPhoto && isLowConf  ? "border-rose-300"
             : "border-slate-200"}`}>
      <div className="flex items-start gap-3">
        {/* Photo thumb */}
        <button type="button"
                data-testid={`meas-${slotKey}-capture`}
                disabled={disabled || uploading}
                onClick={() => inputRef.current?.click()}
                className="shrink-0 w-20 h-20 border border-dashed border-slate-300 rounded-sm bg-slate-50 flex items-center justify-center hover:bg-slate-100 overflow-hidden">
          {uploading ? (
            <CircleNotch size={22} className="animate-spin text-slate-400" />
          ) : photoUrl ? (
            <img src={photoUrl} alt={label || slotKey}
                  className="w-full h-full object-cover" />
          ) : (
            <Camera size={22} className="text-slate-400" />
          )}
        </button>
        <input ref={inputRef} type="file" accept="image/*"
                capture="environment"
                className="hidden"
                onChange={e => uploadPhoto(e.target.files?.[0])}
                data-testid={`meas-${slotKey}-file`} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold text-slate-800">{label || slotKey}</span>
            {required && !hasPhoto && (
              <span className="text-[9px] uppercase tracking-wider bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded-sm font-bold">
                required
              </span>
            )}
          </div>

          {/* Value display */}
          {hasPhoto ? (
            <div className="mt-1">
              <div className="text-base font-bold font-mono text-slate-900 tabular-nums"
                   data-testid={`meas-${slotKey}-value`}>
                {finalValue != null
                  ? `${finalValue} ${unit}`
                  : (aiPending || aiStatus === "pending" || aiStatus === "processing")
                    ? "—"
                    : "—"}
              </div>
              <div className="text-[10px] mt-0.5 flex items-center gap-1 flex-wrap">
                {isOfflineRecord ? (
                  /* iter150AZ.77 — offline stub badge. Replaces all
                     AI / approval status because the photo hasn't
                     touched a server yet. */
                  <span className="inline-flex items-center gap-1 text-sky-800 bg-sky-50 border border-sky-200 px-1.5 py-0.5 rounded-sm font-bold uppercase tracking-wider text-[9px]"
                        data-testid={`meas-${slotKey}-offline-badge`}>
                    <CloudArrowUp size={9} weight="fill" /> saved offline — will sync
                  </span>
                ) : (aiPending || aiStatus === "pending" || aiStatus === "processing") && !isOverride ? (
                  <span className="inline-flex items-center gap-1 text-violet-700"
                        data-testid={`meas-${slotKey}-ai-pending`}>
                    <CircleNotch size={9} weight="fill" className="animate-spin" />
                    Reading the meter…
                  </span>
                ) : isOverride ? (
                  <span className="inline-flex items-center gap-1 text-amber-700">
                    <Pencil size={9} weight="fill" /> manual override
                    {aiValue != null && <span className="text-slate-500">(AI saw {aiValue} {unit})</span>}
                  </span>
                ) : aiValue != null ? (
                  <span className="inline-flex items-center gap-1 text-emerald-700">
                    <CheckCircle size={9} weight="fill" /> AI ·
                    <span className="text-slate-500">{(conf * 100).toFixed(0)}% confidence</span>
                  </span>
                ) : aiStatus === "failed" ? (
                  <span className="inline-flex items-center gap-1 text-rose-700"
                        data-testid={`meas-${slotKey}-ai-failed`}>
                    <Warning size={9} weight="fill" /> AI failed — use manual override
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-rose-700">
                    <Warning size={9} weight="fill" /> AI couldn&apos;t read
                  </span>
                )}
                {isLowConf && !isOfflineRecord && (
                  <span className="inline-flex items-center gap-1 text-amber-700 ml-1">
                    <Warning size={9} weight="fill" /> low confidence
                  </span>
                )}
                {/* iter150AZ.63 — approval state badge. Pending blocks
                    the wizard's Continue gate; approved is the only
                    way past it for the AI-only path. */}
                {hasPhoto && needsApproval && (
                  <span className="inline-flex items-center gap-1 text-violet-800 bg-violet-50 border border-violet-200 px-1.5 py-0.5 rounded-sm font-bold uppercase tracking-wider text-[9px] ml-1"
                        data-testid={`meas-${slotKey}-approval-pending`}>
                    <Warning size={9} weight="fill" /> awaiting your approval
                  </span>
                )}
                {hasPhoto && isApproved && !isOfflineRecord && (
                  <span className="inline-flex items-center gap-1 text-emerald-800 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-sm font-bold uppercase tracking-wider text-[9px] ml-1"
                        data-testid={`meas-${slotKey}-approved-badge`}>
                    <CheckCircle size={9} weight="fill" /> approved
                  </span>
                )}
              </div>
              {record?.ai_notes && (
                <div className="text-[10px] text-slate-500 mt-0.5 italic">
                  {record.ai_notes}
                </div>
              )}
            </div>
          ) : (
            <div className="text-[11px] text-slate-500 mt-1">
              Tap the camera icon — point at the meter LCD and snap a clear photo.
            </div>
          )}

          {/* iter150AZ.63 — mandatory approval gate. The "Approve
              Reading ✓" button must be clicked before the wizard's
              Continue button enables for the AI-only path. Operator
              can also Edit (override with corrected value) or Retake. */}
          {hasPhoto && needsApproval && !disabled && (
            <div className="mt-2 p-2 bg-violet-50 border border-violet-200 rounded-sm"
                 data-testid={`meas-${slotKey}-approval-gate`}>
              <p className="text-[11px] text-violet-900 leading-snug mb-1.5">
                <Warning size={10} weight="fill" className="inline -mt-0.5 mr-0.5 text-violet-700" />
                <span className="font-bold">Confirm the reading.</span>{" "}
                Compare the value above to your meter. Approve only if
                it matches.
              </p>
              <div className="flex items-center gap-1 flex-wrap">
                <button type="button"
                        data-testid={`meas-${slotKey}-approve`}
                        disabled={approving}
                        onClick={approveReading}
                        className="text-[11px] font-bold inline-flex items-center gap-1 px-2.5 py-1 rounded-sm border border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                  {approving
                    ? <CircleNotch size={11} className="animate-spin" />
                    : <CheckCircle size={11} weight="fill" />}
                  Approve {finalValue} {unit}
                </button>
                <button type="button"
                        data-testid={`meas-${slotKey}-edit-before-approve`}
                        onClick={() => {
                          setOverrideInput(finalValue != null ? String(finalValue) : "");
                          setOverrideReason("");
                          setEditingOverride(true);
                        }}
                        className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-amber-400 bg-white text-amber-800 hover:bg-amber-50">
                  <Pencil size={11} /> Edit value
                </button>
                <button type="button"
                        data-testid={`meas-${slotKey}-retake-before-approve`}
                        onClick={() => inputRef.current?.click()}
                        className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
                  <ArrowsClockwise size={11} /> Retake
                </button>
              </div>
            </div>
          )}

          {/* Action buttons */}
          {hasPhoto && !disabled && (
            <div className="mt-2 flex items-center gap-1 flex-wrap">
              <button type="button"
                      data-testid={`meas-${slotKey}-retake`}
                      onClick={() => inputRef.current?.click()}
                      className="text-[10px] inline-flex items-center gap-1 px-1.5 py-1 rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
                <ArrowsClockwise size={10} /> Retake
              </button>
              {/* iter150AZ.77 — override / revert / delete are only
                  meaningful once the photo is on the server (real
                  measurement id with an AI status). Hide them for
                  offline stubs; retake is the only correction
                  available while disconnected. */}
              {!isOfflineRecord && !isOverride && (
                <button type="button"
                        data-testid={`meas-${slotKey}-override`}
                        onClick={() => {
                          setOverrideInput(aiValue != null ? String(aiValue) : "");
                          setOverrideReason("");
                          setEditingOverride(true);
                        }}
                        className="text-[10px] inline-flex items-center gap-1 px-1.5 py-1 rounded-sm border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100">
                  <Pencil size={10} /> Override
                </button>
              )}
              {!isOfflineRecord && isOverride && (
                <button type="button"
                        data-testid={`meas-${slotKey}-revert`}
                        onClick={clearOverride}
                        className="text-[10px] inline-flex items-center gap-1 px-1.5 py-1 rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
                  <ArrowsClockwise size={10} /> Use AI reading
                </button>
              )}
              {!isOfflineRecord && (
                <button type="button"
                        data-testid={`meas-${slotKey}-delete`}
                        onClick={remove}
                        className="text-[10px] inline-flex items-center gap-1 px-1.5 py-1 rounded-sm border border-rose-300 bg-white text-rose-700 hover:bg-rose-50 ml-auto">
                  <X size={10} /> Delete
                </button>
              )}
            </div>
          )}

          {/* Override editor */}
          {editingOverride && (
            <div data-testid={`meas-${slotKey}-override-editor`}
                 className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-sm space-y-1.5">
              <p className="text-[10px] text-amber-800 leading-snug">
                <Warning size={9} weight="fill" className="inline -mt-0.5 mr-0.5" />
                Manual overrides are flagged on the record. Use only when the
                meter is hard to read or AI extraction is wrong.
              </p>
              <div className="flex items-center gap-1">
                <input type="number" step="0.1" inputMode="decimal"
                        data-testid={`meas-${slotKey}-override-input`}
                        value={overrideInput}
                        onChange={e => setOverrideInput(e.target.value)}
                        placeholder={`Reading in ${unit}`}
                        className="text-xs px-2 py-1 border border-amber-300 rounded-sm font-mono flex-1" />
                <span className="text-[11px] text-slate-600 font-mono">{unit}</span>
              </div>
              <input type="text" maxLength={120}
                      data-testid={`meas-${slotKey}-override-reason`}
                      value={overrideReason}
                      onChange={e => setOverrideReason(e.target.value)}
                      placeholder="Reason (e.g. glare on display)"
                      className="text-[11px] px-2 py-1 border border-amber-300 rounded-sm w-full" />
              <div className="flex items-center gap-1">
                <button type="button"
                        data-testid={`meas-${slotKey}-override-cancel`}
                        onClick={() => setEditingOverride(false)}
                        className="text-[10px] px-2 py-1 rounded-sm border border-slate-300 bg-white text-slate-600 hover:bg-slate-50">
                  Cancel
                </button>
                <button type="button"
                        data-testid={`meas-${slotKey}-override-save`}
                        onClick={submitOverride}
                        className="ml-auto text-[10px] px-2 py-1 rounded-sm border border-amber-600 bg-amber-600 text-white hover:bg-amber-700">
                  Save override
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// MeasurementsSet — renders all required voltage + amps slots for one
// commissioning OR warranty-claim record, based on phaseCount (1 | 3).
// Handles the per-flow rules (warranty amps optional, etc.).
// ─────────────────────────────────────────────────────────────────────
export function MeasurementsSet({
  parentKind, parentId, phaseCount, nominalVoltage,
  email, ampsRequired = true, disabled = false,
  // iter150AZ.77 — when the wizard is offline (or just navigated
  // back from a later step) the parent can re-seed our local items
  // with any offline stubs it captured in the same session. Without
  // this prop a remount would forget the stubs and the Continue
  // gate would falsely re-block.
  initialItems,
  onChange,
}) {
  const [items, setItems] = useState(() => Array.isArray(initialItems) ? initialItems : []);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setLoading(true);
    try {
      const r = await api.get(`/measurements`, {
        params: { parent_kind: parentKind, parent_id: parentId },
        headers: { "X-User-Email": email || "" },
      });
      const d = r.data;
      const serverItems = d.items || [];
      // iter150AZ.77 — merge: prefer server records for any slot the
      // server knows about; keep our offline stubs for any slot it
      // doesn't (those are still queued in the SW outbox).
      setItems(prev => {
        const serverSlots = new Set(serverItems.map(it => it.slot));
        const localOffline = (prev || []).filter(
          it => it?._offline && !serverSlots.has(it.slot));
        const merged = [...serverItems, ...localOffline];
        onChange?.(merged);
        return merged;
      });
    } catch (_e) {
      // Offline / transient network error. Keep whatever items we
      // already have (offline stubs from this session); the next
      // online refresh will reconcile.
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    // Defer one tick so the state writes inside refresh() happen
    // outside the synchronous effect body — satisfies
    // react-hooks/set-state-in-effect without changing semantics.
    const handle = setTimeout(() => { refresh(); }, 0);
    return () => clearTimeout(handle);
  }, [parentKind, parentId]);

  function recordFor(slotKey) {
    return items.find(i => i.slot === slotKey) || null;
  }

  function handleSaved(rec) {
    setItems(prev => {
      const next = prev.filter(i => i.slot !== rec.slot);
      next.push(rec);
      onChange?.(next);
      return next;
    });
  }
  function handleCleared(slotKey) {
    setItems(prev => {
      const next = prev.filter(i => i.slot !== slotKey);
      onChange?.(next);
      return next;
    });
  }

  const VOLT_SLOTS = phaseCount === 1
    ? [
        { kind: "voltage", state: "running", phase: "single",
          label: "Voltage — running" },
        { kind: "voltage", state: "stopped", phase: "single",
          label: "Voltage — stopped" },
      ]
    : [
        { kind: "voltage", state: "running", phase: "l1_l2",
          label: "L1 ↔ L2 — running" },
        { kind: "voltage", state: "running", phase: "l2_l3",
          label: "L2 ↔ L3 — running" },
        { kind: "voltage", state: "running", phase: "l1_l3",
          label: "L1 ↔ L3 — running" },
        { kind: "voltage", state: "stopped", phase: "l1_l2",
          label: "L1 ↔ L2 — stopped" },
        { kind: "voltage", state: "stopped", phase: "l2_l3",
          label: "L2 ↔ L3 — stopped" },
        { kind: "voltage", state: "stopped", phase: "l1_l3",
          label: "L1 ↔ L3 — stopped" },
      ];

  const AMP_SLOTS = phaseCount === 1
    ? [
        { kind: "amps", state: "running", phase: "single",
          label: "Amps — running" },
      ]
    : [
        { kind: "amps", state: "running", phase: "l1", label: "Amps L1 — running" },
        { kind: "amps", state: "running", phase: "l2", label: "Amps L2 — running" },
        { kind: "amps", state: "running", phase: "l3", label: "Amps L3 — running" },
      ];

  return (
    <div className="space-y-3" data-testid="measurements-set">
      <section>
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1.5">
          Voltage <span className="text-rose-600 ml-0.5">*</span>
          <span className="ml-2 text-[10px] text-slate-400 normal-case font-normal">
            (photo-only, AI reads the LCD)
          </span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {VOLT_SLOTS.map(s => (
            <MeasurementCapture key={s.kind + s.state + s.phase}
              parentKind={parentKind} parentId={parentId}
              kind={s.kind} state={s.state} phase={s.phase}
              label={s.label}
              nominalHint={nominalVoltage}
              initialRecord={recordFor(_slotKey(s.kind, s.state, s.phase))}
              onSaved={handleSaved}
              onCleared={() => handleCleared(_slotKey(s.kind, s.state, s.phase))}
              required
              email={email}
              disabled={disabled} />
          ))}
        </div>
      </section>

      <section>
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1.5">
          Amps {ampsRequired && <span className="text-rose-600 ml-0.5">*</span>}
          <span className="ml-2 text-[10px] text-slate-400 normal-case font-normal">
            (running only{!ampsRequired ? " — optional, only if the compressor runs" : ""})
          </span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {AMP_SLOTS.map(s => (
            <MeasurementCapture key={s.kind + s.state + s.phase}
              parentKind={parentKind} parentId={parentId}
              kind={s.kind} state={s.state} phase={s.phase}
              label={s.label}
              initialRecord={recordFor(_slotKey(s.kind, s.state, s.phase))}
              onSaved={handleSaved}
              onCleared={() => handleCleared(_slotKey(s.kind, s.state, s.phase))}
              required={ampsRequired}
              email={email}
              disabled={disabled} />
          ))}
        </div>
      </section>

      {loading && (
        <div className="text-[11px] text-slate-400 flex items-center gap-1">
          <CircleNotch size={11} className="animate-spin" /> Loading saved measurements…
        </div>
      )}
    </div>
  );
}

// isMeasurementsSetComplete is re-exported at the top of this file
// from ./measurementCompletion (kept as a pure module so unit tests
// can import it without dragging in axios/SW/WS dependencies).

